import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X, Download } from "lucide-react";
import { BROCHURE_URL } from "../lib/api";
import { useLang } from "../i18n";

const LINKS = [
  { key: "species", href: "#species", id: "species" },
  { key: "heritage", href: "#heritage", id: "heritage" },
  { key: "markets", href: "#markets", id: "markets" },
  { key: "reach", href: "#reach", id: "global-reach" },
];

export const LangToggle = ({ className = "" }) => {
  const { lang, setLang, langs } = useLang();
  return (
    <div className={`flex items-center gap-1 ${className}`} data-testid="lang-toggle">
      {langs.map((l) => (
        <button
          key={l.code}
          data-testid={`lang-toggle-${l.code}`}
          onClick={() => setLang(l.code)}
          className={`rounded-full px-2.5 py-1 font-mono text-[11px] uppercase tracking-wider transition-colors duration-300 ${
            lang === l.code
              ? "bg-brand-blue text-brand-bone"
              : "text-brand-slate hover:text-brand-timber"
          }`}
        >
          {l.label}
        </button>
      ))}
    </div>
  );
};

export const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/" + href);
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.nav
      data-testid="main-navigation"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 z-50 w-full border-b transition-[background-color,border-color] duration-500 ${
        scrolled
          ? "bg-brand-bone/80 backdrop-blur-xl border-border"
          : "bg-transparent border-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-[1600px] items-center justify-between px-6 py-4 md:px-12">
        <a
          href="#top"
          data-testid="nav-logo"
          onClick={(e) => { e.preventDefault(); go("#top"); }}
          className="flex items-center gap-3"
        >
          <img src="/logo.png" alt="7 Seas Group USA" className="h-11 w-auto" />
        </a>

        <div className="hidden items-center gap-10 md:flex">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.href)}
              className="group relative font-mono text-xs uppercase tracking-[0.2em] text-brand-blue transition-colors hover:text-brand-timber"
            >
              {t(`nav.${l.key}`)}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-brand-timber transition-[width] duration-300 group-hover:w-full" />
            </button>
          ))}
        </div>

        <div className="hidden items-center gap-3 md:flex">
          <LangToggle />
          <a
            href={BROCHURE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-brochure-btn"
            className="group inline-flex items-center gap-2 rounded-full border border-brand-blue/30 px-5 py-2.5 font-mono text-xs uppercase tracking-[0.2em] text-brand-blue transition-colors duration-300 hover:border-brand-timber hover:text-brand-timber"
          >
            <Download size={14} />
            {t("nav.brochure")}
          </a>
          <button
            data-testid="nav-quote-btn"
            onClick={() => go("#quote")}
            className="inline-flex items-center rounded-full bg-brand-blue px-6 py-2.5 font-mono text-xs uppercase tracking-[0.2em] text-brand-bone transition-colors duration-300 hover:bg-brand-timber"
          >
            {t("nav.quote")}
          </button>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((o) => !o)}
          className="md:hidden text-brand-blue"
          aria-label="Menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-border bg-brand-bone/95 backdrop-blur-xl md:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {LINKS.map((l) => (
                <button
                  key={l.href}
                  data-testid={`nav-mobile-link-${l.id}`}
                  onClick={() => go(l.href)}
                  className="py-3 text-left font-serif text-3xl text-brand-blue"
                >
                  {t(`nav.${l.key}`)}
                </button>
              ))}
              <LangToggle className="mt-4" />
              <button
                data-testid="nav-mobile-quote-btn"
                onClick={() => go("#quote")}
                className="mt-4 rounded-full bg-brand-blue px-6 py-3 text-center font-mono text-xs uppercase tracking-[0.2em] text-brand-bone"
              >
                {t("nav.quote")}
              </button>
              <a
                href={BROCHURE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-mobile-brochure-btn"
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full border border-brand-blue/30 px-6 py-3 font-mono text-xs uppercase tracking-[0.2em] text-brand-blue"
              >
                <Download size={14} /> {t("nav.downloadBrochure")}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};
